import { transparentize } from 'polished';
import styled from 'styled-components';

import { responsive } from '@main/theme';

import { FlexRowCenterVertical } from '../StyledWrappers';
import { StyleUtils } from '../StyledWrappers/StyleUtils';
import { MenuItemProps, MenuMode, SideMenuWrapperProps } from './types';

/**
 * Wrapper for a menu item tab
 */
export const TabWrapper = styled(FlexRowCenterVertical)`
  width: 100%;
  padding-left: 10px;
  padding-right: 10px;
  gap: 0.5em;
  color: ${({ theme }) => theme.colors.transcendNavy};
  line-height: 18px;
  height: 100%;
`;

/**
 * The SideMenu wrapper
 */
export const SideMenuWrapper = styled.div<SideMenuWrapperProps>`
  height: 100%;
  min-height: ${({ minheight }) => minheight};
  display: ${({ mode }) => (mode === 'horizontal' ? 'flex' : 'block')};
  flex-wrap: ${({ mode }) => (mode === 'horizontal' ? 'wrap' : 'nowrap')};
  ${({ maxheight, mode }) =>
    maxheight
      ? `
    max-height: ${maxheight};
    ${mode === 'inline' ? 'overflow-y: auto;' : ''}
  `
      : ''};

  ${responsive.atLeastTablet} {
    ::after {
      background: linear-gradient(
        ${({ theme }) => transparentize(1, theme.colors.sidebarNavBg)},
        ${({ theme }) => theme.colors.sidebarNavBg} 75%
      );
      content: '';
      display: block;
      height: ${({ havegradient }) =>
        havegradient === 'true' ? '230px' : '0'};
      position: absolute;
      bottom: 25%;
      width: 100%;
      pointer-events: none;
    }
  }
`;

/**
 * Restrict text to a max length
 */
export const MaxLengthWrapper = styled.span`
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-grow: 1;
`;

/**
 * Styled MenuItem component wrapper
 */
export const MenuItemWrapper = styled.div<
  MenuItemProps & {
    /** The menu display mode */
    mode?: MenuMode;
  }
>`
  cursor: pointer;
  ${({ mode, level }) =>
    mode === 'horizontal'
      ? `
      padding: ${StyleUtils.Spacing.xs} ${StyleUtils.Spacing.md};
      padding-left: ${StyleUtils.Spacing.md};
      text-align: center;
    `
      : `
      padding: ${StyleUtils.Spacing.md} ${StyleUtils.Spacing.md};
      padding-left: calc(${StyleUtils.Spacing.md} + ${level || 0} * ${StyleUtils.Spacing.l});
      text-align: left;
    `}
  border-right: 2px solid transparent;
  border-bottom: 2px solid transparent;
  background-color: ${({ isSelected, theme, mode }) =>
    isSelected && mode !== 'horizontal' ? theme.colors.gray4 : 'transparent'};
  color: ${({ isSelected, theme }) =>
    isSelected ? theme.colors.primary : 'inherit'};
  font-weight: ${({ isSelected }) => (isSelected ? 'bold' : 'normal')};
  transition:
    background-color 0.1s ease-in-out,
    color 0.2s ease-in-out,
    border-right-color 0.1s ease-in-out,
    border-bottom-color 0.1s ease-in-out;
  outline: none;

  border-right-color: ${({ isSelected, theme, mode }) =>
    isSelected && mode !== 'horizontal' ? theme.colors.primary : 'transparent'};
  border-bottom-color: ${({ isSelected, theme, mode }) =>
    mode === 'horizontal'
      ? isSelected
        ? theme.colors.primary
        : theme.colors.gray4
      : 'transparent'};

  &:hover {
    background-color: ${({ theme, mode }) =>
      mode !== 'horizontal' ? theme.colors.gray1 : 'transparent'};
    border-bottom-color: ${({ theme, mode }) =>
      mode === 'horizontal' ? theme.colors.primary : 'transparent'};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

/**
 * Styled SubMenu components wrappers
 */
export const SubMenuWrapper = styled.div<{
  /** Indentation level */
  level?: number;
  /** The menu display mode */
  mode?: MenuMode;
}>`
  padding-left: ${({ level, mode }) =>
    mode === 'horizontal'
      ? '0'
      : `calc(${level || 0} * ${StyleUtils.Spacing.l})`};
`;

export const SubMenuTitleWrapper = styled.div<{
  /** Is the submenu currently open? */
  isOpen?: boolean;
  /** The menu display mode */
  mode?: MenuMode;
}>`
  width: 100%;
  cursor: pointer;
  display: flex;
  align-items: center;
  ${({ mode }) =>
    mode === 'horizontal'
      ? `
      padding: ${StyleUtils.Spacing.sm} ${StyleUtils.Spacing.md};
      justify-content: center;
      text-align: center;
    `
      : `
      padding: ${StyleUtils.Spacing.md} ${StyleUtils.Spacing.md};
      justify-content: space-between;
      text-align: left;
    `}

  &:hover {
    background-color: ${({ theme }) => theme.colors.gray1};
  }

  &::after {
    content: '';
    display: ${({ mode }) => (mode === 'horizontal' ? 'none' : 'inline-block')};
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 5px solid currentColor;
    margin-left: 8px;
    transition: transform 0.2s ease-in-out;
    transform: ${({ isOpen }) => (isOpen ? 'rotate(-180deg)' : 'rotate(0deg)')};
  }
`;
